import { BaySizesAndCgs, I20LcgsByBay, IBayPattern } from "@tedivo/tedivo-bay-grid-pure";
import { pad3, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

import { ForeAftEnum } from "open-vessel-definition";
import { createSvgText } from "./createSvgText";
import { handleDoubleClick } from "@tedivo/tedivo-dom-helpers";

interface ISetTopBayNumbersProps {
  blps: BaySizesAndCgs[];
  lcgsBy20Bay: I20LcgsByBay;
  svgNode: SVGElement;
  xPos: (x: number, dec?: number) => number;
  fontColor: string | undefined;
  fontSize?: number;
  onDoubleClick?: (node: SVGElement | HTMLElement) => void;
  doNotRepeatBays?: Set<IBayPattern>;
}

export function createSvgTopBayNumbers({
  blps,
  lcgsBy20Bay,
  svgNode,
  xPos,
  fontColor,
  fontSize = 8,
  onDoubleClick,
  doNotRepeatBays,
}: ISetTopBayNumbersProps): void {
  const blpsByBay: { [bay: IBayPattern]: BaySizesAndCgs } = {};
  blps.forEach((blp) => {
    blpsByBay[blp.isoBay] = blp;
  });

  const allBays = Object.keys(lcgsBy20Bay).sort(sortNumericAsc) as IBayPattern[];

  const midTopNumbers = new Set<IBayPattern>();

  const svgG = document.createElementNS("http://www.w3.org/2000/svg", "g");
  svgG.classList.add("top-bay-numbers");

  allBays
    .filter((bay) => {
      return !doNotRepeatBays || !doNotRepeatBays?.has(bay);
    })
    .forEach((bay) => {
      doNotRepeatBays?.add(bay);

      const lcgsBy20OfBay = lcgsBy20Bay[bay];
      const x = lcgsBy20OfBay.lcgs.get(20)?.lcg;

      if (x === undefined) return;

      if (lcgsBy20OfBay.confirmedPairedBay) {
        const evenBay = pad3(
          Math.round((Number(bay) + Number(lcgsBy20OfBay.confirmedPairedBay)) / 2),
        ) as IBayPattern;

        const evenX = lcgsBy20Bay[lcgsBy20OfBay.confirmedPairedBay].lcgs.get(20)?.lcg;

        if (evenX !== undefined && !midTopNumbers.has(evenBay)) {
          const midX = (x + evenX) / 2;
          const smFontSize = Math.round(fontSize * 1.25);
          const midBayNumberSvgText = createSvgText({
            text: evenBay,
            x: xPos(midX, 2),
            y: Math.round(smFontSize * 1.7),
            fontSize: smFontSize,
            textAnchor: "middle",
            textColor: fontColor || "#aaa",
          });

          midBayNumberSvgText.dataset["bay"] = bay;
          midBayNumberSvgText.style.fontFamily = "Arial, sans-serif";
          midBayNumberSvgText.classList.add("top-bay-number");
          svgG.appendChild(midBayNumberSvgText);

          if (onDoubleClick) {
            handleDoubleClick(midBayNumberSvgText, onDoubleClick);
          }

          midTopNumbers.add(evenBay);
        }
      }

      if (x !== undefined) {
        const bayNumberSvgText = createSvgText({
          text: getDecoratedIsoNumber(blpsByBay[bay] || bay),
          x: xPos(x, 2),
          y: fontSize,
          fontSize,
          textAnchor: "middle",
          textColor: fontColor || "#aaa",
        });

        bayNumberSvgText.dataset["bay"] = bay;
        bayNumberSvgText.style.fontFamily = "Arial, sans-serif";
        bayNumberSvgText.classList.add("top-bay-number");
        svgG.appendChild(bayNumberSvgText);

        if (onDoubleClick) {
          handleDoubleClick(bayNumberSvgText, onDoubleClick);
        }
      }
    });

  function getDecoratedIsoNumber(blp: BaySizesAndCgs | string): string {
    if (typeof blp === "string") return blp;

    if (blp.pairedBay) {
      return blp.pairedBay === ForeAftEnum.FWD ? `${blp.isoBay}&middot;` : `&middot;${blp.isoBay}`;
    }
    return blp.isoBay;
  }

  svgNode.appendChild(svgG);
}
